export const VISUAL_DB_HEADERS = [
  "id",
  "index",
  "group",
  "equivalents",
  "verySimilar",
  "similar",
  "enabled",
];

export const DEFAULT_GROUPS = ["Mayus", "Minus", "Numbers", "Symbols"];

export const DEFAULT_EXPORT_CONFIG = {
  packId: "ImgLetras",
  version: "0.1",
  sheetFilePattern: "{packId}_{group}.png",
  cellW: 512,
  cellH: 512,
  cols: 6,
  pixelsPerUnit: 128,
  pivotX: 0.5,
  pivotY: 0.5,
  origin: "top-left",
  csvIndexIs1Based: true,
  outputJsonFileName: "Pack.json",
  outputCsvFileName: "visual_db.csv",
};

export const EMPTY_VISUAL_ROW = {
  id: "",
  index: 1,
  group: "Mayus",
  equivalents: "",
  verySimilar: "",
  similar: "",
  enabled: true,
};

export function getRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function cleanText(text) {
  return String(text || "")
    .replace(/^\uFEFF/, "")
    .replace(/\u0000/g, "");
}

export function escapeCSVValue(value) {
  const stringValue = String(value ?? "");

  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

export function parseCSV(text) {
  const rows = [];
  let currentRow = [];
  let currentValue = "";
  let insideQuotes = false;

  const clean = cleanText(text);

  for (let index = 0; index < clean.length; index += 1) {
    const char = clean[index];
    const nextChar = clean[index + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      currentValue += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }

      currentRow.push(currentValue);

      if (currentRow.some((cell) => String(cell).trim() !== "")) {
        rows.push(currentRow);
      }

      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += char;
  }

  if (currentValue !== "" || currentRow.length > 0) {
    currentRow.push(currentValue);

    if (currentRow.some((cell) => String(cell).trim() !== "")) {
      rows.push(currentRow);
    }
  }

  return rows;
}

export function normalizeHeader(header) {
  const value = String(header || "").trim();

  const match = VISUAL_DB_HEADERS.find(
    (item) => item.toLowerCase() === value.toLowerCase()
  );

  return match || value;
}

export function splitList(value) {
  if (!value || !String(value).trim()) return [];

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function uniqueList(values) {
  return [...new Set(values.map((item) => String(item).trim()).filter(Boolean))];
}

export function joinList(values) {
  return uniqueList(values).join(",");
}

export function addIdToCsvList(currentValue, idToAdd) {
  if (!idToAdd) return currentValue || "";

  const current = splitList(currentValue);

  if (current.includes(idToAdd)) {
    return joinList(current);
  }

  return joinList([...current, idToAdd]);
}

export function removeIdFromCsvList(currentValue, idToRemove) {
  return joinList(splitList(currentValue).filter((item) => item !== idToRemove));
}

export function mirrorFieldForRows(rows, fieldName) {
  const editableRows = rows.map((row) => ({
    ...row,
    [fieldName]: joinList(splitList(row[fieldName])),
  }));

  const rowMap = new Map(editableRows.map((row) => [row.id, row]));

  editableRows.forEach((row) => {
    const relatedIds = splitList(row[fieldName]);

    relatedIds.forEach((relatedId) => {
      const relatedRow = rowMap.get(relatedId);

      if (!relatedRow) return;

      relatedRow[fieldName] = addIdToCsvList(relatedRow[fieldName], row.id);
    });
  });

  return editableRows;
}

export function mirrorAllRelationFields(rows) {
  let nextRows = mirrorFieldForRows(rows, "equivalents");
  nextRows = mirrorFieldForRows(nextRows, "verySimilar");
  nextRows = mirrorFieldForRows(nextRows, "similar");

  return nextRows;
}

export function normalizeVisualRow(row) {
  return {
    _rowId: row._rowId || getRandomId(),
    id: String(row.id || "").trim(),
    index: Math.max(1, Number.parseInt(row.index, 10) || 1),
    group: String(row.group || "").trim(),
    equivalents: joinList(splitList(row.equivalents)),
    verySimilar: joinList(splitList(row.verySimilar)),
    similar: joinList(splitList(row.similar)),
    enabled:
      row.enabled === true ||
      String(row.enabled || "").trim().toLowerCase() === "true",
  };
}

export function createEmptyVisualRow(overrides = {}) {
  return normalizeVisualRow({
    ...EMPTY_VISUAL_ROW,
    ...overrides,
    _rowId: getRandomId(),
  });
}

export function csvRowsToVisualRows(parsedRows) {
  if (!parsedRows.length) return [];

  const headers = parsedRows[0].map(normalizeHeader);

  const missingHeaders = VISUAL_DB_HEADERS.filter(
    (requiredHeader) => !headers.includes(requiredHeader)
  );

  if (missingHeaders.length > 0) {
    throw new Error(`Faltan columnas: ${missingHeaders.join(", ")}`);
  }

  return parsedRows
    .slice(1)
    .map((row) => {
      const objectRow = {
        _rowId: getRandomId(),
      };

      headers.forEach((header, index) => {
        objectRow[header] = row[index] ?? "";
      });

      return normalizeVisualRow(objectRow);
    })
    .filter((row) => row.id);
}

export function buildCSV(headers, rows) {
  const lines = [];

  lines.push(headers.map(escapeCSVValue).join(","));

  rows.forEach((row) => {
    lines.push(
      headers
        .map((header) => {
          if (header === "enabled") {
            return escapeCSVValue(row.enabled ? "TRUE" : "FALSE");
          }

          return escapeCSVValue(row[header] ?? "");
        })
        .join(",")
    );
  });

  return lines.join("\n");
}

export function downloadTextFile(fileName, content, mimeType = "text/plain") {
  const blob = new Blob([content], {
    type: `${mimeType};charset=utf-8`,
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();

  link.remove();
  URL.revokeObjectURL(url);
}

export function validateVisualRows(rows) {
  const errors = [];

  const ids = rows.map((row) => row.id.trim()).filter(Boolean);

  const duplicatedIds = ids.filter(
    (id, index, array) => array.indexOf(id) !== index
  );

  if (duplicatedIds.length > 0) {
    errors.push(`IDs duplicados: ${[...new Set(duplicatedIds)].join(", ")}`);
  }

  rows.forEach((row, index) => {
    const number = index + 1;

    if (!row.id.trim()) {
      errors.push(`Fila ${number}: id vac�o.`);
    }

    if (!row.group.trim()) {
      errors.push(`Fila ${number}: group vac�o.`);
    }

    if (!Number.isFinite(Number(row.index)) || Number(row.index) < 1) {
      errors.push(`Fila ${number}: index inv�lido.`);
    }
  });

  return errors;
}

export function buildPackJson(rows, config) {
  const validationErrors = validateVisualRows(rows);

  if (validationErrors.length > 0) {
    throw new Error(validationErrors[0]);
  }

  const groupsUsed = new Set();

  const enabledItems = rows
    .filter((row) => row.enabled)
    .map((row) => {
      const itemIndex = config.csvIndexIs1Based
        ? Number(row.index) - 1
        : Number(row.index);

      groupsUsed.add(row.group);

      return {
        id: row.id,
        group: row.group,
        index: itemIndex,
        equivalents: splitList(row.equivalents),
        verySimilar: splitList(row.verySimilar),
        similar: splitList(row.similar),
      };
    });

  enabledItems.sort((a, b) => {
    const groupCompare = a.group.localeCompare(b.group);

    if (groupCompare !== 0) return groupCompare;

    return a.index - b.index;
  });

  const sheets = Array.from(groupsUsed)
    .sort((a, b) => a.localeCompare(b))
    .map((group) => ({
      key: group,
      image: config.sheetFilePattern
        .replaceAll("{packId}", config.packId)
        .replaceAll("{group}", group),
      cellW: Number(config.cellW),
      cellH: Number(config.cellH),
      cols: Number(config.cols),
      pixelsPerUnit: Number(config.pixelsPerUnit),
      pivotX: Number(config.pivotX),
      pivotY: Number(config.pivotY),
    }));

  return {
    packId: config.packId,
    version: config.version,
    origin: config.origin,
    sheets,
    items: enabledItems,
  };
}

export function getNextIndex(rows) {
  if (!rows.length) return 1;

  return (
    Math.max(
      ...rows.map((row) => Number.parseInt(row.index, 10)).filter(Boolean)
    ) + 1
  );
}